// Real Learn enrolments, backed by Supabase (Postgres + RLS).
// One row per (member, course); UNIQUE(user_id, course_id) enforces
// duplicate prevention at the DB layer — a repeat POST comes back 409 and
// we treat it as a successful "already enrolled" so the UI stays in sync.
// A trigger awards +50 Ihsan Points on enrol and reverses on cancel; a
// public counts table exposes an anonymous "N enrolled" number without
// leaking who.
import { getAccessToken, SUPABASE_ANON } from './session';

const PGREST_URL = (process.env.REACT_APP_POSTGREST_URL
  || 'https://yuftrqcahapyrblxvkns.supabase.co/rest/v1').replace(/\/$/, '');

async function authHeaders(extra = {}) {
  const token = await getAccessToken();
  if (!token) return null;
  return { apikey: SUPABASE_ANON, authorization: `Bearer ${token}`, ...extra };
}

// Enrol the current member in a course. Returns { ok, already?, error? }.
// A duplicate is treated as success — the UI's target state is "enrolled",
// and the DB already reflects that.
export async function enrolInCourse(courseId, courseTitle) {
  const headers = await authHeaders({ 'content-type': 'application/json', Prefer: 'return=minimal' });
  if (!headers) return { ok: false, error: 'Please sign in first.' };
  const body = JSON.stringify({
    course_id: String(courseId),
    course_title: courseTitle || null,
  });
  let lastErr = '';
  // Retry transient 5xx (PostgREST cold-start) up to 3x — the same pattern
  // that keeps volunteer sign-ups reliable.
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await fetch(`${PGREST_URL}/learn_enrolments`, { method: 'POST', headers, body });
      if (res.status === 409) return { ok: true, already: true };
      if (res.ok) return { ok: true };
      lastErr = (await res.text().catch(() => '')) || `HTTP ${res.status}`;
      if (res.status !== 503 && res.status !== 502 && res.status !== 504) break;
    } catch (e) {
      lastErr = String(e);
    }
    await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
  }
  return { ok: false, error: lastErr || 'Could not record your enrolment.' };
}

// Undo an enrolment. Returns { ok }. RLS scopes the DELETE to the current
// member's own row, so we don't need a client-side user filter.
export async function cancelEnrolment(courseId) {
  const headers = await authHeaders();
  if (!headers) return { ok: false };
  try {
    const res = await fetch(
      `${PGREST_URL}/learn_enrolments?course_id=eq.${encodeURIComponent(String(courseId))}`,
      { method: 'DELETE', headers },
    );
    return { ok: res.ok };
  } catch {
    return { ok: false };
  }
}

// The member's own enrolments, newest first. Empty array when not signed in.
export async function getMyEnrolments() {
  const headers = await authHeaders();
  if (!headers) return [];
  try {
    const res = await fetch(
      `${PGREST_URL}/learn_enrolments?select=course_id,course_title,status,created_at&order=created_at.desc`,
      { headers },
    );
    if (!res.ok) return [];
    return (await res.json()) || [];
  } catch {
    return [];
  }
}

export async function hasEnrolled(courseId) {
  const headers = await authHeaders();
  if (!headers) return false;
  try {
    const res = await fetch(
      `${PGREST_URL}/learn_enrolments?select=course_id&course_id=eq.${encodeURIComponent(String(courseId))}`,
      { headers },
    );
    if (!res.ok) return false;
    return ((await res.json()) || []).length > 0;
  } catch {
    return false;
  }
}

// Public enrolment count for one course. Anonymous — the counts table exposes
// no user data. Returns a number, or null if unavailable.
export async function getEnrolmentCount(courseId) {
  try {
    const res = await fetch(
      `${PGREST_URL}/learn_counts?select=enrolments&course_id=eq.${encodeURIComponent(String(courseId))}`,
      { headers: { apikey: SUPABASE_ANON } },
    );
    if (!res.ok) return null;
    const rows = (await res.json()) || [];
    return rows.length ? rows[0].enrolments : 0;
  } catch {
    return null;
  }
}

// Public catalogue of real MTFA classes — served from Supabase `public.courses`
// (RLS: SELECT active=true to anon+authenticated). This is the source of truth
// for dates, fees and descriptions. The Directus/mock catalogue still supplies
// programme details and imagery; OfferingsProvider overlays these rows on top
// by matching `id`.
export async function listCourses() {
  try {
    const res = await fetch(
      `${PGREST_URL}/courses?select=id,title,description,category,level,format,duration_text,location,org,starts_at,ends_at,price_cents,capacity,badge,image_url,is_lms,sort&active=eq.true&order=sort`,
      { headers: { apikey: SUPABASE_ANON } },
    );
    if (!res.ok) return [];
    return (await res.json()) || [];
  } catch {
    return [];
  }
}
